import React, { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Mic,
} from "lucide-react";

/**
 * Loop nudge controls for fine-tuning A-B loop regions
 * Replaces the notepad tab bar when loop is active
 *
 * Controls:
 * - Increment selector (x5, x10) - controls the step size for all operations
 * - Expand left (ChevronsLeft) - widen loop from left side
 * - Contract left (ChevronRight) - shrink loop from left side (move left edge right)
 * - Contract right (ChevronLeft) - shrink loop from right side (move right edge left)
 * - Expand right (ChevronsRight) - widen loop from right side
 *
 * Button order: Expand Left | Contract Left | Contract Right | Expand Right
 */
const LoopNudgeControls = ({
  onExpandLeft,
  onExpandRight,
  onContractLeft,
  onContractRight,
  disabled = false,
  isMobile = false,
  onToggleRecording = null,
  isRecording = false,
  inline = false,
}) => {
  const [increment, setIncrement] = useState(5); // 5 or 10

  const buttonBaseClass =
    "flex items-center justify-center rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  const buttonSizeClass = isMobile ? "p-0.5" : "w-7 h-7";

  const buttonClass = `${buttonBaseClass} ${buttonSizeClass} bg-forest-700 hover:bg-forest-600 text-forest-200 border border-forest-600`;

  const iconStyle = isMobile ? { width: "14px", height: "14px" } : undefined;
  const iconClass = isMobile ? "" : "w-4 h-4";

  const activeIncrementClass = "bg-gold-500 text-forest-900 border-gold-400";

  const inactiveIncrementClass =
    "bg-forest-800 text-forest-300 border-forest-600 hover:bg-forest-700";

  // Inline mode (desktop): just buttons, no container/labels/mic
  if (inline) {
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => setIncrement(5)}
          className={`px-1.5 py-0.5 text-xs font-medium rounded border transition-colors ${
            increment === 5 ? activeIncrementClass : inactiveIncrementClass
          }`}
          title="Medium step (0.5 seconds)"
        >
          x5
        </button>
        <button
          onClick={() => setIncrement(10)}
          className={`px-1.5 py-0.5 text-xs font-medium rounded border transition-colors ${
            increment === 10 ? activeIncrementClass : inactiveIncrementClass
          }`}
          title="Large step (1.0 seconds)"
        >
          x10
        </button>
        <button
          onClick={() => onExpandLeft && onExpandLeft(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Expand loop left by ${increment * 0.1}s`}
        >
          <ChevronsLeft className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onContractLeft && onContractLeft(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Contract loop from left by ${increment * 0.1}s`}
        >
          <ChevronRight className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onContractRight && onContractRight(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Contract loop from right by ${increment * 0.1}s`}
        >
          <ChevronLeft className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onExpandRight && onExpandRight(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Expand loop right by ${increment * 0.1}s`}
        >
          <ChevronsRight className={iconClass} style={iconStyle} />
        </button>
      </div>
    );
  }

  // Full mode (mobile): bar with labels and mic button
  return (
    <div className={`flex items-center ${isMobile ? "gap-1" : "justify-between gap-2"} px-2 py-1.5 border-b border-forest-700 bg-forest-900`}>
      {/* Left side: Increment selector */}
      <div className="flex items-center gap-1">
        {!isMobile && (
          <span className="text-xs mr-1 text-forest-300">Step:</span>
        )}
        <button
          onClick={() => setIncrement(5)}
          className={`${isMobile ? "px-1" : "px-2"} py-0.5 text-xs font-medium rounded border transition-colors ${
            increment === 5 ? activeIncrementClass : inactiveIncrementClass
          }`}
          title="Medium step (0.5 seconds)"
        >
          x5
        </button>
        <button
          onClick={() => setIncrement(10)}
          className={`${isMobile ? "px-1" : "px-2"} py-0.5 text-xs font-medium rounded border transition-colors ${
            increment === 10 ? activeIncrementClass : inactiveIncrementClass
          }`}
          title="Large step (1.0 seconds)"
        >
          x10
        </button>
      </div>

      {/* Record button - between increment and size controls */}
      {onToggleRecording && (
        <button
          onClick={onToggleRecording}
          className={`${buttonBaseClass} ${buttonSizeClass} transition-colors ${
            isRecording
              ? "bg-ember-500 text-forest-900 border border-ember-400"
              : "bg-forest-700 hover:bg-forest-600 text-ember-500 border border-forest-600"
          }`}
          title={isRecording ? "Stop recording" : "Start recording"}
        >
          <Mic className={iconClass} style={iconStyle} />
        </button>
      )}

      {/* Right side: Expand/Contract controls */}
      {/* Order: Expand Left | Contract Left | Contract Right | Expand Right */}
      <div className={`flex items-center ${isMobile ? "gap-0.5" : "gap-1"}`}>
        {!isMobile && (
          <span className="text-xs mr-1 text-forest-300">Size:</span>
        )}
        <button
          onClick={() => onExpandLeft && onExpandLeft(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Expand loop left by ${increment * 0.1}s`}
        >
          <ChevronsLeft className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onContractLeft && onContractLeft(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Contract loop from left by ${increment * 0.1}s`}
        >
          <ChevronRight className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onContractRight && onContractRight(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Contract loop from right by ${increment * 0.1}s`}
        >
          <ChevronLeft className={iconClass} style={iconStyle} />
        </button>
        <button
          onClick={() => onExpandRight && onExpandRight(increment)}
          disabled={disabled}
          className={buttonClass}
          title={`Expand loop right by ${increment * 0.1}s`}
        >
          <ChevronsRight className={iconClass} style={iconStyle} />
        </button>
      </div>
    </div>
  );
};

export default LoopNudgeControls;
